import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge, EmptyState } from "../../components/ui-extras";

/**
 * Admin Fuel-Tax-Rates editor (operator spec 2026-05-22).
 *
 * Edits the per-litre (Province × Product) rates that drive the
 * FET / PFT / PCT / LOCAL columns on customer transaction reports.
 *
 * Source of truth: `fuel_tax_rates` Mongo collection. When the
 * collection is empty the backend falls back to a hardcoded
 * `FUEL_TAX_PER_L_TABLE` constant — use the "Seed defaults" button
 * to populate the table for the first time.
 */
const CA_PROVINCES = [
  "AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU",
  "ON", "PE", "QC", "SK", "YT",
];

const KNOWN_PRODUCTS = ["DIESEL_LS", "DSL_EFF_LS", "DEFD", "RFR", "ULSD", "GAS"];

// Hardcoded fallback table — mirrors `backend/tax.py::FUEL_TAX_PER_L_TABLE`.
// Used to render a "Customised" pill on any row whose live rates differ
// from the system-default published spec.
const DEFAULT_RATES = {
  "AB|DIESEL_LS":  { fet: 0,     pft: 0.13,  pct: 0, local: 0 },
  "AB|DSL_EFF_LS": { fet: 0,     pft: 0.13,  pct: 0, local: 0 },
  "AB|RFR":        { fet: 0,     pft: 0.13,  pct: 0, local: 0 },
  "BC|DIESEL_LS":  { fet: 0,     pft: 0.15,  pct: 0, local: 0 },
  "BC|DSL_EFF_LS": { fet: 0,     pft: 0.09,  pct: 0, local: 0.185 },
  "BC|RFR":        { fet: 0,     pft: 0.15,  pct: 0, local: 0 },
  "MB|DIESEL_LS":  { fet: 0,     pft: 0.125, pct: 0, local: 0 },
  "MB|DSL_EFF_LS": { fet: 0,     pft: 0.125, pct: 0, local: 0 },
  "MB|RFR":        { fet: 0,     pft: 0.125, pct: 0, local: 0 },
  "ON|DIESEL_LS":  { fet: 0,     pft: 0.09,  pct: 0, local: 0 },
  "ON|DSL_EFF_LS": { fet: 0,     pft: 0.09,  pct: 0, local: 0 },
  "ON|RFR":        { fet: 0,     pft: 0.09,  pct: 0, local: 0 },
  "ON|GAS":        { fet: 0,     pft: 0.09,  pct: 0, local: 0 },
  "QC|DSL_EFF_LS": { fet: 0,     pft: 0.202, pct: 0, local: 0 },
  "QC|RFR":        { fet: 0,     pft: 0.202, pct: 0, local: 0 },
  "SK|DIESEL_LS":  { fet: 0,     pft: 0.15,  pct: 0, local: 0 },
  "SK|DSL_EFF_LS": { fet: 0,     pft: 0.15,  pct: 0, local: 0 },
  "SK|RFR":        { fet: 0,     pft: 0.15,  pct: 0, local: 0 },
};

function diffFromDefault(row) {
  const def = DEFAULT_RATES[`${row.province}|${row.product}`];
  if (!def) return { customised: false, def: null, diffs: [] };
  const diffs = ["fet", "pft", "pct", "local"]
    .filter((k) => Math.abs((row[k] ?? 0) - (def[k] ?? 0)) > 1e-9)
    .map((k) => ({ key: k, live: row[k] ?? 0, def: def[k] ?? 0 }));
  return { customised: diffs.length > 0, def, diffs };
}

const RATE_COLS = [
  { key: "fet", label: "FET ($/L)" },
  { key: "pft", label: "PFT ($/L)" },
  { key: "pct", label: "PCT ($/L)" },
  { key: "local", label: "LOCAL/VTT ($/L)" },
];


function AddRowModal({ onClose, onSaved }) {
  const [province, setProvince] = useState("ON");
  const [product, setProduct] = useState("DIESEL_LS");
  const [fet, setFet] = useState("0");
  const [pft, setPft] = useState("0");
  const [pct, setPct] = useState("0");
  const [local, setLocal] = useState("0");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/fuel-tax-rates", {
        province, product,
        fet: parseFloat(fet) || 0,
        pft: parseFloat(pft) || 0,
        pct: parseFloat(pct) || 0,
        local: parseFloat(local) || 0,
      });
      toast.success(`Added ${province} / ${product}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center"
      data-testid="ftr-add-modal"
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-lg p-6 w-full max-w-md space-y-4"
      >
        <h3 className="text-lg font-semibold">Add Fuel-Tax-Rate row</h3>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            Province
            <select
              data-testid="ftr-add-province"
              className="mt-1 w-full border rounded px-2 py-1"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
            >
              {CA_PROVINCES.map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            Product
            <select
              data-testid="ftr-add-product"
              className="mt-1 w-full border rounded px-2 py-1"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
            >
              {KNOWN_PRODUCTS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          {[["FET", fet, setFet, "fet"], ["PFT", pft, setPft, "pft"],
            ["PCT", pct, setPct, "pct"], ["LOCAL", local, setLocal, "local"]
          ].map(([lbl, v, set, k]) => (
            <label key={k} className="block text-sm">
              {lbl} ($/L)
              <input
                type="number"
                step="0.0001"
                min="0"
                data-testid={`ftr-add-${k}`}
                className="mt-1 w-full border rounded px-2 py-1"
                value={v}
                onChange={(e) => set(e.target.value)}
              />
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid="ftr-add-cancel"
            className="px-3 py-1.5 text-sm border rounded"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="ftr-add-save"
            className="px-3 py-1.5 text-sm bg-zinc-900 text-white rounded disabled:opacity-50"
          >
            {saving ? "Saving…" : "Add row"}
          </button>
        </div>
      </form>
    </div>
  );
}


function RateCell({ row, k, onSaved }) {
  const [value, setValue] = useState(String(row[k] ?? 0));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(String(row[k] ?? 0));
    setDirty(false);
  }, [row[k]]);

  const persist = async () => {
    const num = parseFloat(value);
    if (Number.isNaN(num) || num < 0) {
      toast.error("Rate must be a non-negative number");
      setValue(String(row[k] ?? 0));
      setDirty(false);
      return;
    }
    if (num === row[k]) {
      setDirty(false);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/fuel-tax-rates/${row.rate_id}`, { [k]: num });
      toast.success(`${row.province}/${row.product} ${k.toUpperCase()} = ${num}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
      setValue(String(row[k] ?? 0));
    } finally {
      setSaving(false);
      setDirty(false);
    }
  };

  return (
    <input
      type="number"
      step="0.0001"
      min="0"
      data-testid={`ftr-cell-${row.province}-${row.product}-${k}`}
      className={`w-24 text-right border rounded px-2 py-1 text-sm ${dirty ? "ring-2 ring-amber-400" : ""}`}
      value={value}
      disabled={saving}
      onChange={(e) => { setValue(e.target.value); setDirty(true); }}
      onBlur={persist}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
    />
  );
}


export default function FuelTaxRates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [filterProv, setFilterProv] = useState("");
  const [filterProd, setFilterProd] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fuel-tax-rates");
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load rates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) =>
      (!filterProv || r.province === filterProv)
      && (!filterProd || r.product === filterProd)
    );
  }, [rows, filterProv, filterProd]);

  const seed = async (force) => {
    if (force && !window.confirm(
      "Force-seed will OVERWRITE every existing row with the system default values. " +
      "Custom edits will be lost. Continue?",
    )) return;
    try {
      const url = `/fuel-tax-rates/seed-defaults${force ? "?force=true" : ""}`;
      const { data } = await api.post(url);
      toast.success(
        `Seed complete — inserted ${data.inserted}, updated ${data.updated}, ` +
        `skipped ${data.skipped}.`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed failed");
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(
      `Delete the ${row.province} / ${row.product} row? The lookup will ` +
      `fall back to the system default for this position.`,
    )) return;
    try {
      await api.delete(`/fuel-tax-rates/${row.rate_id}`);
      toast.success(`Removed ${row.province}/${row.product}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="fuel-tax-rates-page">
      <PageHeader
        testId="ftr-header"
        title="Fuel-Tax Rates"
        subtitle="Per-litre FET / PFT / PCT / LOCAL ($/L)"
        actions={
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="ftr-add-button"
              className="px-3 py-1.5 text-sm bg-zinc-900 text-white rounded"
              onClick={() => setAdding(true)}
            >
              + Add row
            </button>
            <button
              data-testid="ftr-seed-button"
              className="px-3 py-1.5 text-sm border rounded"
              onClick={() => seed(false)}
            >
              Seed defaults (fill missing)
            </button>
            <button
              data-testid="ftr-force-seed-button"
              className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded"
              onClick={() => seed(true)}
            >
              Force-reset to defaults
            </button>
          </div>
        }
      />
      <SectionPad>
        <p className="text-sm text-zinc-500 -mt-3 mb-4 max-w-3xl">
          Rates here drive NET PRICE and the per-litre tax columns on customer
          transaction reports. Edits take effect on the next report request.
        </p>
        <div className="mb-4 flex gap-3 flex-wrap items-center">
          <label className="text-sm">
            Province
            <select
              data-testid="ftr-filter-province"
              className="ml-2 border rounded px-2 py-1"
              value={filterProv}
              onChange={(e) => setFilterProv(e.target.value)}
            >
              <option value="">All</option>
              {CA_PROVINCES.map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          <label className="text-sm">
            Product
            <select
              data-testid="ftr-filter-product"
              className="ml-2 border rounded px-2 py-1"
              value={filterProd}
              onChange={(e) => setFilterProd(e.target.value)}
            >
              <option value="">All</option>
              {KNOWN_PRODUCTS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          <Badge variant="muted" testId="ftr-count-badge">
            {filtered.length} of {rows.length} rows
          </Badge>
        </div>
        {loading ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No rate rows yet"
            hint="Click 'Seed defaults' to populate the table with the system-published rates. Or 'Add row' to enter a new (Province × Product) pair manually."
          />
        ) : (
          <div className="overflow-x-auto bg-white border rounded">
            <table
              data-testid="ftr-table"
              className="min-w-full text-sm"
            >
              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-3 py-2 text-left">Province</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  {RATE_COLS.map((c) => (
                    <th key={c.key} className="px-3 py-2 text-right">{c.label}</th>
                  ))}
                  <th className="px-3 py-2 text-left">Updated</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const diff = diffFromDefault(row);
                  return (
                  <tr
                    key={row.rate_id}
                    data-testid={`ftr-row-${row.province}-${row.product}`}
                    className="border-t hover:bg-zinc-50"
                  >
                    <td className="px-3 py-2 font-mono">
                      <div className="flex items-center gap-2">
                        {row.province}
                        {diff.customised && (
                          <span
                            data-testid={`ftr-customised-${row.province}-${row.product}`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-800 font-sans cursor-help"
                            title={
                              diff.def
                                ? `Edited from system default — ${diff.diffs
                                    .map((d) => `${d.key.toUpperCase()}: ${d.def} → ${d.live}`)
                                    .join(" · ")}`
                                : "Custom row (no system default for this position)"
                            }
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                            customised
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono">{row.product}</td>
                    {RATE_COLS.map((c) => (
                      <td key={c.key} className="px-3 py-2 text-right">
                        <RateCell row={row} k={c.key} onSaved={load} />
                      </td>
                    ))}
                    <td className="px-3 py-2 text-xs text-zinc-500">
                      <div>{row.updated_at?.slice(0, 16).replace("T", " ")}</div>
                      <div className="truncate max-w-[160px]">{row.updated_by}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        data-testid={`ftr-delete-${row.province}-${row.product}`}
                        className="text-xs text-red-600 hover:underline"
                        onClick={() => removeRow(row)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionPad>
      {adding && <AddRowModal onClose={() => setAdding(false)} onSaved={load} />}
    </div>
  );
}
